import styled from '@emotion/styled/macro'
import { Box, Flex } from '@rebass/grid/emotion'
import { UserSettings } from 'components/UserSettings/UserSettings'
import { SearchProps } from 'hooks/useSearch'
import { themeColor, themeFont } from 'mixins'
import {
  Breadcrumb,
  IBreadcrumbItem,
  PersonaSize,
  SearchBox,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTheme } from 'theme'
import logo from '../../assets/porsche.svg'
import { appBarHeight, appNavigationWidth } from '../constants'
import { UserPersona } from '../UserPersona'
import { UserPersonaProps } from '../UserPersona/UserPersona'

const Root = styled(Flex)`
  background: ${themeColor('neutralPrimary')};
  height: ${appBarHeight}px;
  color: ${themeColor('white')};

  &,
  &:hover {
    .ms-Persona-primaryText {
      color: ${themeColor('white')};
    }
    .ms-Persona-secondaryText {
      color: ${themeColor('neutralLighter')};
    }
  }
`

const LogoContainer = styled(Box)`
  background: ${themeColor('neutralSecondary')};
  width: ${appBarHeight}px;
  height: ${appBarHeight}px;
  overflow: hidden;
  text-align: center;

  img {
    height: 100%;
  }
`

const Title = styled.h1`
  ${themeFont('xLarge')};
  font-weight: normal;
  text-align: center;
`

const BreadcrumbContainer = styled(Flex)`
  > * {
    flex: 1;
    min-width: 240px;
  }
`
const SearchContainer = styled(Flex)`
  justify-content: center;
  > * {
    max-width: 480px;
    flex: 1;
  }
`
const AppBarBreadcrumb = styled(Breadcrumb)`
  margin: 0;

  .ms-Breadcrumb-item,
  .ms-Breadcrumb-chevron,
  .ms-Breadcrumb-itemLink,
  .ms-Breadcrumb-overflowButton {
    color: ${themeColor('white')};
  }

  .ms-Breadcrumb-listItem:last-child {
    .ms-Breadcrumb-itemLink {
      color: ${themeColor('neutralLighter')};
    }
  }

  .ms-Breadcrumb-itemLink:hover {
    color: ${themeColor('themeLight')};
    background: transparent;
  }
  .ms-Breadcrumb-itemLink:active:hover {
    color: ${themeColor('neutralLight')};
  }
`

export type AppBarProps = Pick<UserPersonaProps, 'person'> & {
  breadcrumbItems?: IBreadcrumbItem[]
  searchProps?: SearchProps
}
export let CalendarContext: any
export const AppBar: React.FunctionComponent<AppBarProps> = ({
  breadcrumbItems,
  searchProps,
  person,
}) => {
  const theme = useTheme()

  const [showUserSettings, setShowUserSettings] = React.useState(false)
  CalendarContext = React.createContext({
    toggleCalendarDisplay: setShowUserSettings,
  })
  return (
    <Root alignItems="center" py={1} justifyContent="space-between" pl={0}>
      <Flex width={appNavigationWidth} alignItems="center" pr={2}>
        <LogoContainer p={1} mr={2}>
          <img src={logo} alt="Porsche Logo" />
        </LogoContainer>
        <Title>EMOS</Title>
      </Flex>
      {breadcrumbItems && (
        <BreadcrumbContainer flex="1" mr={2}>
          <AppBarBreadcrumb items={breadcrumbItems} maxDisplayedItems={3} />
        </BreadcrumbContainer>
      )}
      {searchProps && (
        <SearchContainer flex="1">
          <SearchBox {...searchProps} />
        </SearchContainer>
      )}
      {person && (
        <Box mr={2}>
          <UserPersona
            person={person}
            hidePersonaDetails={true}
            size={PersonaSize.size32}
            initialsColor={theme.palette.themeDark}
            onClick={() => setShowUserSettings(true)}
          />
        </Box>
      )}
      <UserSettings
        isOpen={showUserSettings}
        onDismiss={() => setShowUserSettings(false)}
      />
    </Root>
  )
}

export default AppBar
